import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

const PAGE_SIZE = 25;

const STATUS_META = Object.freeze({
  new: { label: "Nuevo", color: "success" },
  changed: { label: "Con cambios", color: "info" },
  unchanged: { label: "Sin cambios", color: "default" },
  review: { label: "Revisar", color: "warning" },
});

function displayValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Sí" : "No";
  return String(value);
}

function RowCard({ row, selected, onToggle }) {
  const [expanded, setExpanded] = React.useState(false);
  const meta = STATUS_META[row.status] || STATUS_META.review;
  const selectable = Boolean(row.selectableAction);
  const differences = Array.isArray(row.differences) ? row.differences : [];

  return (
    <Paper
      variant="outlined"
      role={selectable ? "checkbox" : undefined}
      aria-checked={selectable ? selected : undefined}
      tabIndex={selectable ? 0 : -1}
      onClick={() => selectable && onToggle(row)}
      onKeyDown={(event) => {
        if (!selectable || !["Enter", " "].includes(event.key)) return;
        event.preventDefault();
        onToggle(row);
      }}
      sx={{
        p: { xs: 1.5, sm: 1.75 },
        borderRadius: 2.5,
        cursor: selectable ? "pointer" : "default",
        borderWidth: selected ? 2 : 1,
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: selected ? "action.selected" : "background.paper",
        transition: "border-color .15s ease, background-color .15s ease",
        "&:focus-visible": selectable ? { outline: "3px solid", outlineColor: "primary.light", outlineOffset: 2 } : undefined,
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950, overflowWrap: "anywhere" }}>
              {row.values?.name || row.existingName || `Fila ${row.row}`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
              {row.values?.code ? `Código ${row.values.code}` : `Fila ${row.row} · sin código`}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
            {row.matchType === "base" ? <Chip size="small" variant="outlined" label="Ya existe" /> : null}
            <Chip size="small" color={meta.color} label={meta.label} />
            {selected ? <Chip size="small" color="primary" label={row.selectableAction === "update" ? "Actualizar" : "Seleccionado"} /> : null}
          </Stack>
        </Stack>

        {row.values?.description ? (
          <Typography variant="body2" color="text.secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {row.values.description}
          </Typography>
        ) : null}

        <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
          {row.values?.price !== undefined ? (
            <Typography variant="body2"><strong>Venta:</strong> {displayValue(row.values.price)}</Typography>
          ) : null}
          {row.values?.supplierCost !== undefined ? (
            <Typography variant="body2"><strong>Costo proveedor:</strong> {displayValue(row.values.supplierCost)}</Typography>
          ) : null}
          {row.existingName && row.status !== "new" ? (
            <Typography variant="body2" color="text.secondary"><strong>WebProfit:</strong> {row.existingName}</Typography>
          ) : null}
        </Stack>

        {row.matchType === "variant" ? (
          <Alert severity="warning" sx={{ py: 0.25 }}>
            Coincide con una variante existente. No se actualizará el producto base.
          </Alert>
        ) : null}
        {row.status === "changed" && row.matchType === "base" && !row.selectableAction ? (
          <Alert severity="info" sx={{ py: 0.25 }}>
            Este ítem ya existe y tu rol no permite actualizarlo. Se conservará sin cambios.
          </Alert>
        ) : null}
        {row.actionError ? <Alert severity="error" sx={{ py: 0.25 }}>{row.actionError}</Alert> : null}
        {Array.isArray(row.errors) && row.errors.length ? (
          <Alert severity="warning" sx={{ py: 0.25 }}>{row.errors[0].message}</Alert>
        ) : null}

        {differences.length ? (
          <>
            <Button
              size="small"
              variant="text"
              endIcon={<ExpandMoreRoundedIcon sx={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform .15s ease" }} />}
              onClick={(event) => {
                event.stopPropagation();
                setExpanded((current) => !current);
              }}
              sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 900, minHeight: 40 }}
            >
              {expanded ? "Ocultar cambios" : `Ver ${differences.length} cambio${differences.length === 1 ? "" : "s"}`}
            </Button>
            <Collapse in={expanded} unmountOnExit>
              <Stack spacing={0.75} divider={<Divider flexItem />}>
                {differences.map((difference) => (
                  <Box key={`${row.row}-${difference.field}`}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>{difference.label}</Typography>
                    <Typography variant="body2">
                      {displayValue(difference.current)} <strong>→</strong> {displayValue(difference.proposed)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Collapse>
          </>
        ) : null}

        {selectable ? (
          <Typography variant="caption" color={selected ? "primary.main" : "text.secondary"} sx={{ fontWeight: 850 }}>
            {selected
              ? "Tocá la tarjeta para quitarlo de la propuesta."
              : row.selectableAction === "update"
                ? "Tocá la tarjeta para aceptar esta actualización."
                : "Tocá la tarjeta para incluir este producto."}
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

export default function ImportSupplierSelectionStep({ rows = [], rowActions = {}, onActionsChange }) {
  const [search, setSearch] = React.useState("");
  const [status, setStatus] = React.useState("all");
  const [page, setPage] = React.useState(1);

  const normalizedSearch = search.trim().toLocaleLowerCase("es");
  const filtered = React.useMemo(() => rows.filter((row) => {
    if (status !== "all" && row.status !== status) return false;
    if (!normalizedSearch) return true;
    const haystack = [row.values?.name, row.values?.code, row.values?.description, row.existingName]
      .map((value) => String(value || "").toLocaleLowerCase("es"))
      .join(" ");
    return haystack.includes(normalizedSearch);
  }), [normalizedSearch, rows, status]);

  React.useEffect(() => setPage(1), [normalizedSearch, status]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  React.useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const visibleRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const selectedCount = Object.values(rowActions || {}).filter((action) => ["create", "update"].includes(action)).length;
  const selectableFiltered = filtered.filter((row) => row.selectableAction);

  const toggleRow = (row) => {
    const key = String(row.row);
    const next = { ...(rowActions || {}) };
    if (next[key]) delete next[key];
    else next[key] = row.selectableAction;
    onActionsChange(next);
  };

  const selectFiltered = () => {
    const next = { ...(rowActions || {}) };
    selectableFiltered.forEach((row) => { next[String(row.row)] = row.selectableAction; });
    onActionsChange(next);
  };

  const clearFiltered = () => {
    const next = { ...(rowActions || {}) };
    filtered.forEach((row) => { delete next[String(row.row)]; });
    onActionsChange(next);
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 950 }}>Elegí qué productos vendés</Typography>
        <Typography variant="body2" color="text.secondary">
          La lista del proveedor es una propuesta. Nada queda seleccionado masivamente por defecto y los productos existentes sólo cambian si aceptás su actualización.
        </Typography>
      </Box>

      <Alert severity="info">
        Seleccionados: <strong>{selectedCount.toLocaleString("es-AR")}</strong> de {rows.length.toLocaleString("es-AR")} filas. La selección se mantiene aunque busques, filtres o cambies de página.
      </Alert>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <TextField
          fullWidth
          label="Buscar nombre o código"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <TextField
          select
          label="Estado"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          sx={{ minWidth: { sm: 190 } }}
        >
          <MenuItem value="all">Todos</MenuItem>
          <MenuItem value="new">Nuevos</MenuItem>
          <MenuItem value="changed">Con cambios</MenuItem>
          <MenuItem value="unchanged">Sin cambios</MenuItem>
          <MenuItem value="review">Revisar</MenuItem>
        </TextField>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Button variant="outlined" onClick={selectFiltered} disabled={!selectableFiltered.length} sx={{ minHeight: 44, textTransform: "none", fontWeight: 900 }}>
          Seleccionar todos ({selectableFiltered.length})
        </Button>
        <Button variant="text" onClick={clearFiltered} disabled={!filtered.length} sx={{ minHeight: 44, textTransform: "none", fontWeight: 900 }}>
          Quitar selección del filtro
        </Button>
      </Stack>

      {!visibleRows.length ? <Alert severity="info">No hay filas que coincidan con este filtro.</Alert> : null}
      <Stack spacing={1}>
        {visibleRows.map((row) => (
          <RowCard
            key={row.row}
            row={row}
            selected={Boolean(rowActions?.[String(row.row)])}
            onToggle={toggleRow}
          />
        ))}
      </Stack>

      {pageCount > 1 ? (
        <Stack alignItems="center" spacing={0.5}>
          <Pagination count={pageCount} page={page} onChange={(_event, value) => setPage(value)} siblingCount={0} boundaryCount={1} />
          <Typography variant="caption" color="text.secondary">
            {filtered.length.toLocaleString("es-AR")} resultado(s) en el filtro actual
          </Typography>
        </Stack>
      ) : null}
    </Stack>
  );
}
